import InfoTooltip from "@ui/info-tooltip";
import { cn } from "@u/cn";
import { type LucideIcon } from "lucide-react";
import { type ReactNode } from "react";
import { Link } from "react-router-dom";

interface StatCardProps {
  icon: LucideIcon;
  label: string;
  value: React.ReactNode;
  /** Optional info icon next to the label; explanation shown in a tooltip. */
  info?: string;
  /** Small caption under the value. */
  subtext?: ReactNode;
  /** If set, the subtext becomes a link. */
  subtextLink?: string;
  /** If set, the subtext becomes an in-page button (takes precedence over link). */
  subtextOnClick?: () => void;
  /** Optional progress bar (e.g. plan usage). */
  progress?: { value: number; max: number | null };
  /** Show the top accent bar. Default true. */
  accent?: boolean;
  /** Compact horizontal chip (LeadsPage look) vs the default tall card. */
  density?: "default" | "compact";
  /** When set, the (compact) card becomes a quick-filter toggle button. */
  onClick?: () => void;
  active?: boolean;
  className?: string;
}

/**
 * Canonical KPI / stat card. Single source for the surface-card + accent-bar
 * + icon-chip + value pattern (OverviewPage, AnalyticsPage, admin).
 */
export function StatCard({
  icon: Icon,
  label,
  info,
  value,
  subtext,
  subtextLink,
  subtextOnClick,
  progress,
  density = "default",
  onClick,
  active,
  className,
}: StatCardProps) {
  if (density === "compact") {
    const infoNode = info && <InfoTooltip text={info} />;
    const subCls =
      "ml-auto shrink-0 truncate max-w-[55%] label-nano text-muted-foreground uppercase tracking-wide";
    const subNode = subtext ? (
      subtextLink ? (
        <Link to={subtextLink} className={cn(subCls, "hover:text-primary transition-colors")}>
          {subtext}
        </Link>
      ) : (
        <span className={subCls}>{subtext}</span>
      )
    ) : null;
    const compactInner = (
      <>
        <div className="shrink-0 bg-primary/10 dark:bg-primary/20 rounded-lg p-1">
          <Icon size={12} strokeWidth={1.5} className="text-primary" />
        </div>
        <span className="font-anton text-lg tabular-nums leading-none shrink-0">
          {value}
        </span>
        <span className="label-meta text-muted-foreground uppercase tracking-wide truncate">
          {label}
        </span>
        {subNode}
      </>
    );
    const compactBase =
      "surface-card px-3 py-2 flex items-center gap-2";
    if (!onClick)
      return (
        <div className={cn(compactBase, className)}>
          {compactInner}
          {infoNode && <span className="ml-auto shrink-0">{infoNode}</span>}
        </div>
      );
    // Clickable card: overlay button behind content so the ℹ tooltip trigger
    // is a sibling, not a <button> nested inside a <button> (invalid HTML).
    return (
      <div
        className={cn(
          compactBase,
          "relative",
          active && "ring-1 ring-foreground/30",
          className,
        )}
      >
        <button
          type="button"
          onClick={onClick}
          aria-pressed={active}
          aria-label={label}
          className="absolute inset-0 rounded-2xl transition-colors hover:bg-muted/30"
        />
        <div className="pointer-events-none relative flex items-center gap-2.5">
          {compactInner}
        </div>
        {infoNode && (
          <span className="relative ml-auto shrink-0">{infoNode}</span>
        )}
      </div>
    );
  }

  const remaining =
    progress && progress.max !== null ? progress.max - progress.value : null;
  const pct =
    progress && progress.max !== null
      ? Math.round((progress.value / progress.max) * 100)
      : null;

  return (
    <div
      className={cn(
        "surface-card p-5 flex flex-col gap-3",
        className,
      )}
    >
      <div className="flex items-center gap-2">
        <Icon size={14} strokeWidth={1.5} className="shrink-0 text-muted-foreground" />
        <span className="text-xs font-mono uppercase tracking-widest text-muted-foreground">
          {label}
        </span>
        {info && (
          <span className="ml-auto shrink-0">
            <InfoTooltip text={info} />
          </span>
        )}
      </div>

      <p className="text-3xl font-anton tabular-nums leading-none truncate">
        {value}
      </p>

      {subtext && subtextOnClick && (
        <button
          type="button"
          onClick={subtextOnClick}
          className="text-left text-xs font-mono text-muted-foreground uppercase tracking-wide -mt-1 hover:text-primary transition-colors"
        >
          {subtext}
        </button>
      )}
      {subtext && !subtextOnClick && !subtextLink && (
        <p className="text-xs font-mono text-muted-foreground uppercase tracking-wide -mt-1">
          {subtext}
        </p>
      )}
      {subtext && !subtextOnClick && subtextLink && (
        <Link
          to={subtextLink}
          className="text-xs font-mono text-muted-foreground uppercase tracking-wide -mt-1 hover:text-primary transition-colors"
        >
          {subtext}
        </Link>
      )}

      {progress && progress.max !== null && (
        <div className="flex flex-col gap-1.5 -mt-1">
          <div
            role="progressbar"
            aria-valuenow={progress.value}
            aria-valuemin={0}
            aria-valuemax={progress.max}
            className="h-2 rounded-full bg-muted/50 overflow-hidden"
          >
            <div
              className="h-full bg-primary rounded-full transition-all duration-700"
              style={{ width: `${Math.min(pct ?? 0, 100)}%` }}
            />
          </div>
          {remaining !== null && (
            <p className="text-xs font-mono text-muted-foreground uppercase tracking-wide">
              {remaining} restante{remaining !== 1 ? "s" : ""} ({pct}%)
            </p>
          )}
        </div>
      )}
    </div>
  );
}
